import UsersPage from '../UsersPage';
import { ProfileCircleBulkIcon } from '../../../icons-lib';
import DialogAbstract from '../../DialogAbstract';
import { GridDataAdapter } from '../../../adapters/GridDataAdapter';

export default class SmeAdminsPage extends UsersPage {
    constructor(props) {
        super(props);

        this.Name = 'smeAdmins';
        this.Server = props.context.getState().server.users;

        this.headerStartIcon = ProfileCircleBulkIcon;

        /* -----------------------------
         * Dynamic role-based filtering
         * --------------------------- */
        // Initialize baseFilterRequest with role IDs instead of system role strings
        this.initializeBaseFilterRequest();

        /* ------------ Search fields ------------- */
        this.searchFields = ['firstName', 'email', 'smeCompany.name'];

        /* ensure permission checks use users controller */
        this.controllerName = 'users';

        /* --------------- Grid Head -------------- */
        this.Head = [
            {
                id: 'email',
                label: 'email',
                alignRight: false,
                cell: 'Text',
                required: true
            },
            {
                id: 'firstName',
                label: 'name',
                alignRight: false,
                cell: 'Text'
            },
            {
                id: 'roleId',
                label: 'role',
                alignRight: false,
                cell: 'SelectLabel',
                from: 'roles',
                display: 'name',
                enumType: true,
                object: 'role',
                filterCell: 'Context',
                filterBy: 'roles',
                required: true,
                itemsFilter: ['sme_admin', 'sme_employee']
            },
            {
                id: 'smeCompanyId',
                sortField: 'smeCompany.name',
                label: 'campaignName', // translation key
                alignRight: false,
                cell: 'Associated',
                from: 'smeCompanies',
                searchType: 'server',
                searchFields: ['name', 'email'],
                searchController: 'sme-companies',
                display: 'name',
                object: 'smeCompany',
                associatedSettings: {
                    primary: 'name',
                    secondary: 'email'
                },
                required: true
            },
            {
                id: 'dateCreated',
                label: 'dateCreated',
                alignRight: false,
                cell: 'Date',
                filterCell: 'Date',
                hideOnAdd: true,
                readOnly: true
            },
            {
                id: 'dateModified',
                label: 'dateModified',
                alignRight: false,
                cell: 'Date',
                filterCell: 'Date',
                disableEditor: true
            }
        ];
    }

    /* -----------------------
     *  Custom Functions
     * ---------------------- */

    // Initialize base filter request with role IDs instead of system role strings
    async initializeBaseFilterRequest() {
        try {
            // Get roles from context or fetch them
            let roles = this.props.context.getState().controllersData.roles;

            if (!roles || roles.length === 0) {
                // Fetch roles if not available in context
                const result = await this.props.context
                    .getState()
                    .server.roles.getAll();
                roles = result.data || [];
            }

            // Find SME admin role ID
            const smeAdminRole = roles.find(
                (role) => role.systemRole === 'sme_admin'
            );

            if (smeAdminRole) {
                // Set base filter request using role ID
                this.baseFilterRequest = [
                    {
                        field: 'roleId',
                        op: '=',
                        value: smeAdminRole.id
                    }
                ];
            } else {
                // Fallback to system role string if role not found
                this.baseFilterRequest = [
                    {
                        field: 'role.systemRole',
                        op: '=',
                        value: 'sme_admin'
                    }
                ];
            }

            // Trigger data retrieval if the page is already mounted
            if (this.state && this.state.isLoading !== undefined) {
                this.retrieveData();
            }
        } catch (error) {
            console.error('Failed to initialize base filter request:', error);
            // Fallback to system role string if role IDs can't be fetched
            this.baseFilterRequest = [
                {
                    field: 'role.systemRole',
                    op: '=',
                    value: 'sme_admin'
                }
            ];
        }
    }

    // Override componentDidMount to ensure proper initialization
    async componentDidMount() {
        // Initialize base filter request first
        await this.initializeBaseFilterRequest();

        // Call parent componentDidMount
        super.componentDidMount();
    }

    // Override menu items to only show edit and delete
    renderCustomMenuItems() {
        return null; // This will only show the default edit and delete options
    }

    getCreateFields() {
        return [
            {
                id: 'firstName',
                label: 'name',
                cell: 'Text',
                required: true
            },
            {
                id: 'email',
                label: 'email',
                cell: 'Text',
                required: true
            },
            {
                id: 'isActive',
                label: 'isActive',
                cell: 'Boolean',
                defaultValue: true,
                type: 'boolean',
                hideInEditor: true
            },
            {
                id: 'twoFactorEnabled',
                label: 'twoFactor',
                cell: 'Boolean',
                defaultValue: false,
                type: 'boolean',
                hideInEditor: true
            },
            {
                id: 'emailVerified',
                label: 'emailVerified',
                cell: 'Boolean',
                defaultValue: true,
                type: 'boolean',
                hideInEditor: true
            },
            {
                id: 'language',
                label: 'language',
                cell: 'Items',
                items: ['en'],
                defaultValue: 'en',
                required: true,
                hideInEditor: true
            },
            {
                id: 'roleId',
                label: 'role',
                cell: 'Select',
                from: 'roles',
                display: 'name',
                enumType: true,
                object: 'role',
                required: true,
                itemsFilter: ['sme_admin', 'sme_employee']
            },
            {
                id: 'smeCompanyId',
                label: 'campaignName',
                cell: 'Associated',
                from: 'smeCompanies',
                searchType: 'server',
                searchFields: ['name', 'email'],
                searchController: 'sme-companies',
                display: 'name',
                object: 'smeCompany',
                associatedSettings: { primary: 'name', secondary: 'email' },
                required: true
            }
        ];
    }

    getEditFields() {
        return [
            {
                id: 'firstName',
                label: 'name',
                cell: 'Text',
                required: true
            },
            {
                id: 'email',
                label: 'email',
                cell: 'Text',
                required: true
            },
            {
                id: 'isActive',
                label: 'isActive',
                cell: 'Boolean',
                defaultValue: true,
                type: 'boolean',
                hideInEditor: true
            },
            {
                id: 'twoFactorEnabled',
                label: 'twoFactor',
                cell: 'Boolean',
                defaultValue: false,
                type: 'boolean',
                hideInEditor: true
            },
            {
                id: 'emailVerified',
                label: 'emailVerified',
                cell: 'Boolean',
                defaultValue: true,
                type: 'boolean',
                hideInEditor: true
            },
            {
                id: 'language',
                label: 'language',
                cell: 'Items',
                items: ['en'],
                defaultValue: 'en',
                required: true,
                hideInEditor: true
            },
            {
                id: 'roleId',
                label: 'role',
                cell: 'Select',
                from: 'roles',
                display: 'name',
                enumType: true,
                object: 'role',
                required: true,
                itemsFilter: ['sme_admin', 'sme_employee']
            },
            {
                id: 'smeCompanyId',
                label: 'campaignName',
                cell: 'Associated',
                from: 'smeCompanies',
                searchType: 'server',
                searchFields: ['name', 'email'],
                searchController: 'sme-companies',
                display: 'name',
                object: 'smeCompany',
                associatedSettings: {
                    primary: 'name',
                    secondary: 'email'
                }
            },
            {
                id: 'dateCreated',
                label: 'dateCreated',
                cell: 'Date',
                readOnly: true,
                hideOnAdd: true
            }
        ];
    }

    dialogOpen(data) {
        const isEditMode = !!(data && data.id);
        const fields = isEditMode
            ? this.getEditFields()
            : this.getCreateFields();
        const dialogData = GridDataAdapter.transform(data, fields);
        this.setState({
            dialogOpen: true,
            dialogData,
            isEditMode: isEditMode || false,
            open: false
        });
    }

    renderDialog() {
        const fields = this.state?.isEditMode
            ? this.getEditFields()
            : this.getCreateFields();
        return (
            <DialogAbstract
                key={`sme-admin-dialog-${
                    this.state.isEditMode ? 'edit' : 'add'
                }`}
                dialogOpen={this.state.dialogOpen}
                dialogData={this.state.dialogData}
                permissions={this.permissions}
                Name={this.Name}
                Head={fields}
                Server={this.Server}
                onSave={(data) => this.handleDialogOnSave(data)}
                onClose={() => this.handleDialogOnClose()}
            />
        );
    }

    // Override handleDialogOnSave to include currentRoleId for SME admin updates
    async handleDialogOnSave(data) {
        // If this is an edit operation (user has an ID), include currentRoleId
        if (data.id) {
            // Get the current user data to find their current role
            const currentUserData = this.List.find(
                (user) => user.id === data.id
            );
            if (currentUserData && currentUserData.roleId) {
                data.currentRoleId = currentUserData.roleId;
            }
        }

        // Call the parent save method
        this.setState({ dialogOpen: false, dialogData: null });
        await this.retrieveData();
    }
}
